<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="project-modal"
    :style="modalStyle"
    @mousedown="bringToFront"
  >
    <!-- Header (draggable) -->
    <div
      ref="headerRef"
      class="modal-header"
      :class="{ flashing: isFlashing }"
      @mousedown="startDrag"
    >
      <span class="modal-title">{{ projectData?.name || (contractAddress ? `${contractAddress.substring(0, 8)}...${contractAddress.substring(contractAddress.length - 6)}` : 'Project Details') }}</span>
      <div class="header-buttons">
        <button class="minimize-btn" @click="minimize" title="Minimize"><MinusIcon class="btn-icon" /></button>
        <button class="close-btn" @click="close" title="Close"><XMarkIcon class="btn-icon" /></button>
      </div>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <div v-if="projectData" class="project-details">
        <!-- Dapp Header: Icon + Info side by side -->
        <div class="dapp-header">
          <a v-if="projectData.img && projectData.website" :href="projectData.website" target="_blank" class="dapp-icon clickable">
            <img :src="projectData.img" :alt="projectData.name" @error="handleImageError" />
          </a>
          <div v-else-if="projectData.img" class="dapp-icon">
            <img :src="projectData.img" :alt="projectData.name" @error="handleImageError" />
          </div>
          <div v-else class="dapp-icon">
            <img :src="DEFAULT_LOGO" alt="MegaETH" />
          </div>
          <div class="dapp-info">
            <div class="dapp-name-row">
              <a v-if="projectData.website" :href="projectData.website" target="_blank" class="dapp-name clickable">{{ projectData.name }}</a>
              <div v-else class="dapp-name">{{ projectData.name }}</div>
              <span v-if="projectIsMegaMafia" class="megamafia-badge">MegaMafia</span>
            </div>
            <div v-if="projectData.category" class="dapp-category">{{ projectData.category }}</div>
            <p v-if="projectData.description" class="dapp-description">{{ projectData.description }}</p>
            <div class="dapp-links">
              <a v-if="projectData.twitter" :href="`https://twitter.com/${projectData.twitter}`" target="_blank" class="dapp-link" title="Twitter">
                @{{ projectData.twitter }}
              </a>
              <a v-if="projectData.website" :href="projectData.website" target="_blank" class="dapp-link" title="Website">
                Visit Website
              </a>
            </div>
          </div>
        </div>

        <!-- Circle Action Section -->
        <!-- Position Saved indicator (has circle + saved) -->
        <div v-if="hasCircle && isSaved" class="saved-indicator">
          <BookmarkIconSolid class="saved-icon" />
          <span class="saved-text">Position Saved</span>
          <button class="unsave-btn" @click="handleUnsave" title="Remove saved position">
            <XMarkIcon class="unsave-icon" />
          </button>
        </div>

        <!-- Save Position button (has circle + not saved) -->
        <button v-else-if="hasCircle && !isSaved" class="action-btn save-position-btn" @click="handleSavePosition">
          <BookmarkIcon class="action-icon" />
          <span>Save Position</span>
        </button>

        <!-- Add Circle button (tracked but no circle yet) -->
        <button v-else-if="!hasCircle && contractAddress !== 'not-tracked'" class="action-btn add-circle-btn" @click="handleAddCircle">
          <PlusCircleIcon class="action-icon" />
          <span>Add Circle</span>
        </button>

        <!-- Not Tracked Yet indicator -->
        <div v-if="contractAddress === 'not-tracked'" class="not-tracked-section">
          <div class="not-tracked-message">
            <ClockIcon class="not-tracked-icon" />
            <span class="not-tracked-text">Not tracked yet</span>
          </div>
          <p class="not-tracked-description">This project doesn't have a tracked contract address yet. Stats will appear once tracking begins.</p>
        </div>

        <!-- Performance Metrics (hidden when not tracked) -->
        <div v-if="metrics && contractAddress !== 'not-tracked'" class="metrics-section">
          <div class="section-title">STATS</div>

          <div class="stat-row odd">
            <div class="stat-info">
              <span class="label">TX/SEC:</span>
              <span class="value">{{ formattedTPS }}</span>
            </div>
            <button class="pin-btn" @click="togglePin('tps')" title="Pin to header">
              <PlusCircleIconSolid v-if="isPinned('tps')" class="pin-icon pinned" />
              <PlusCircleIcon v-else class="pin-icon" />
            </button>
          </div>

          <div class="stat-row even">
            <div class="stat-info">
              <span class="label">GAS/SEC:</span>
              <span class="value">{{ formattedGPS }}</span>
            </div>
            <button class="pin-btn" @click="togglePin('gps')" title="Pin to header">
              <PlusCircleIconSolid v-if="isPinned('gps')" class="pin-icon pinned" />
              <PlusCircleIcon v-else class="pin-icon" />
            </button>
          </div>
        </div>

        <!-- Delete Circle Section (hidden when not tracked) -->
        <div v-if="contractAddress !== 'not-tracked'" class="delete-section">
          <button class="delete-btn" @click="handleDelete" title="Hide this circle">
            <TrashIcon class="delete-icon" />
            <span>Hide Circle</span>
          </button>
          <span class="delete-note">Hidden circles can be restored in Settings</span>
        </div>
      </div>

      <!-- No project data - just show address (with OLI label info if available) -->
      <div v-else class="address-only">
        <!-- Address Header -->
        <div class="dapp-header">
          <div class="dapp-icon">
            <img :src="DEFAULT_LOGO" alt="MegaETH" />
          </div>
          <div class="dapp-info">
            <div class="dapp-name">{{ displayName }}</div>
            <div v-if="formattedCategory" class="dapp-category">{{ formattedCategory }}</div>
            <a :href="`https://mega.etherscan.io/address/${contractAddress}`" target="_blank" class="dapp-address">
              {{ contractAddress }}
            </a>
          </div>
        </div>

        <!-- Saved Position Indicator -->
        <div v-if="isSaved" class="saved-indicator">
          <BookmarkIconSolid class="saved-icon" />
          <span class="saved-text">Position Saved</span>
          <button class="unsave-btn" @click="handleUnsave" title="Remove saved position">
            <XMarkIcon class="unsave-icon" />
          </button>
        </div>

        <!-- Performance Metrics -->
        <div v-if="metrics" class="metrics-section">
          <div class="section-title">STATS</div>

          <div class="stat-row odd">
            <div class="stat-info">
              <span class="label">TX/SEC:</span>
              <span class="value">{{ formattedTPS }}</span>
            </div>
            <button class="pin-btn" @click="togglePin('tps')" title="Pin to header">
              <PlusCircleIconSolid v-if="isPinned('tps')" class="pin-icon pinned" />
              <PlusCircleIcon v-else class="pin-icon" />
            </button>
          </div>

          <div class="stat-row even">
            <div class="stat-info">
              <span class="label">GAS/SEC:</span>
              <span class="value">{{ formattedGPS }}</span>
            </div>
            <button class="pin-btn" @click="togglePin('gps')" title="Pin to header">
              <PlusCircleIconSolid v-if="isPinned('gps')" class="pin-icon pinned" />
              <PlusCircleIcon v-else class="pin-icon" />
            </button>
          </div>
        </div>

        <!-- Delete Circle Section -->
        <div class="delete-section">
          <button class="delete-btn" @click="handleDelete" title="Hide this circle">
            <TrashIcon class="delete-icon" />
            <span>Hide Circle</span>
          </button>
          <span class="delete-note">Hidden circles can be restored in Settings</span>
        </div>
      </div>

    </div>

    <!-- Resize Handles -->
    <div class="resize-handle resize-right" @mousedown.stop="startResize('right', $event)"></div>
    <div class="resize-handle resize-bottom" @mousedown.stop="startResize('bottom', $event)"></div>
    <div class="resize-handle resize-corner" @mousedown.stop="startResize('corner', $event)"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, type Ref } from 'vue';
import { XMarkIcon, PlusCircleIcon, BookmarkIcon, TrashIcon, MinusIcon, ClockIcon, PlusCircleIconSolid, BookmarkIconSolid } from '@/components/icons';
import type { Project } from '@/data/projects';
import { getOliLabelForAddress, getProjectByName, type OliLabel, DEFAULT_LOGO, isMegaMafia } from '@/data/projects';
import type { CircleMetrics } from '@/rendering/CircleRenderer';
import { userSettings, makeAddressStatKey, type AddressStatType } from '@/stores/userSettings';
import { useModalDragResize } from '@/composables/useModalDragResize';

// Props
export interface ProjectModalProps {
  zIndexBase?: number;
  modalId?: string; // Unique identifier for this modal instance
}

const props = withDefaults(defineProps<ProjectModalProps>(), {
  zIndexBase: 1000,
  modalId: ''
});

// Emits
const emit = defineEmits<{
  close: [modalId: string];
  focus: [modalId: string];
  minimize: [modalId: string, title: string]; // Emitted when user wants to minimize the modal
  unsave: [address: string]; // Emitted when user wants to unsave a circle
  delete: [address: string, projectName: string | undefined]; // Emitted when user wants to delete a circle
  addCircle: [address: string]; // Emitted when user wants to add a circle for this address
  savePosition: [address: string]; // Emitted when user wants to save circle position
}>();

// Use shared drag/resize composable
const {
  x,
  y,
  width,
  height,
  zIndex,
  modalStyle,
  isFlashing,
  flashHeader,
  setZIndex,
  startDrag: composableStartDrag,
  startResize: composableStartResize
} = useModalDragResize({
  initialWidth: 480,
  initialHeight: 500,
  minWidth: 300,
  minHeight: 200,
  zIndexBase: props.zIndexBase
});

// State
const isOpen = ref(false);
const projectData: Ref<Project | null> = ref(null);
const contractAddress = ref<string>(''); // Contract address for this circle
const projectId = ref<string>(''); // Track which project this modal is displaying
const metrics: Ref<CircleMetrics | null> = ref(null); // Performance metrics for this circle (reactive, updates live)
const isSaved = ref(false); // Whether the circle position is saved (user manually placed)
const hasCircle = ref(false); // Whether a circle exists for this address

// Saved status getter (for live updates)
let savedStatusGetter: (() => boolean) | null = null;

// Has circle getter (for live updates)
let hasCircleGetter: (() => boolean) | null = null;

// Metrics update interval
let metricsGetter: (() => CircleMetrics | null) | null = null;
let metricsUpdateInterval: number | null = null;

// Refs
const modalRef = ref<HTMLElement>();
const headerRef = ref<HTMLElement>();

// Wrap composable functions to pass headerRef
const startDrag = (e: MouseEvent) => composableStartDrag(e, headerRef.value);
const startResize = (mode: 'right' | 'bottom' | 'corner', e?: MouseEvent) => {
  if (e) composableStartResize(mode, e);
};

// Get OLI label for unknown contracts (computed for reactivity)
const oliLabel = computed((): OliLabel | undefined => {
  if (!contractAddress.value) return undefined;
  return getOliLabelForAddress(contractAddress.value);
});

/**
 * Format a slug name to display name (e.g., "kumbaya-xyz" -> "Kumbaya")
 * Removes common suffixes like -xyz, -io, -app, -fi, -finance
 * Capitalizes first letter of each word
 */
function formatDisplayName(slug: string): string {
  if (!slug) return 'Unknown';
  // Remove common domain-like suffixes
  let name = slug
    .replace(/[-_](xyz|io|app|fi|finance|protocol|labs|network|exchange|dex|swap)$/i, '')
    .replace(/[-_]/g, ' ');
  // Title case
  return name.split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Get display name for unknown contracts - try to find project data first
const displayName = computed(() => {
  // Try to get proper project data from OLI name
  const oliProjectName = oliLabel.value?.projectName;
  if (oliProjectName) {
    const project = getProjectByName(oliProjectName);
    if (project?.name) return project.name;
    // Fall back to formatted slug
    return formatDisplayName(oliProjectName);
  }
  // Use contract name if available
  if (oliLabel.value?.contractName) return oliLabel.value.contractName;
  return 'Unknown Contract';
});

// Format category for display (e.g., "fungible_tokens" -> "Fungible Tokens")
const formattedCategory = computed(() => {
  const cat = oliLabel.value?.category;
  if (!cat) return undefined;
  return cat.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
});

// Check if this project is a MegaMafia partner
const projectIsMegaMafia = computed(() => {
  const name = projectData.value?.name;
  if (!name) return false;
  return isMegaMafia(name);
});

// Computed formatted metrics (efficient - only recalculates when metrics change)
const formattedTPS = computed(() => {
  return metrics.value?.tps.toFixed(2) ?? '0.00';
});

const formattedGPS = computed(() => {
  const gps = metrics.value?.gps ?? 0;
  if (gps >= 1_000_000) return (gps / 1_000_000).toFixed(1) + 'M';
  if (gps >= 1_000) return (gps / 1_000).toFixed(1) + 'K';
  if (gps >= 10) return gps.toFixed(0);
  return gps.toFixed(1);
});

/**
 * Handle image load error - fallback to default logo
 */
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  if (img && img.src !== DEFAULT_LOGO) {
    img.src = DEFAULT_LOGO;
  }
};

/**
 * Open modal with project data at mouse position
 * Signature options:
 * 1. Old (click on circle): open(project, address, mouseX, mouseY, metricsData, getMetrics, savedStatus, getSavedStatus)
 * 2. New (from search): open(project, address, metricsData, x, y, getMetrics?, circleExists?, getHasCircle?, savedStatus?)
 */
const open = (
  project: Project | null,
  address: string,
  mouseXOrMetrics: number | CircleMetrics,
  mouseYOrX?: number,
  metricsDataOrY?: CircleMetrics | number,
  getMetrics?: (() => CircleMetrics | null) | undefined,
  savedStatusOrCircleExists: boolean = false,
  getSavedStatusOrGetHasCircle?: (() => boolean) | undefined,
  circleExistsOrSavedStatus: boolean = false
) => {
  projectData.value = project;
  contractAddress.value = address;
  projectId.value = address; // Use address as unique identifier

  // Handle both old signature (mouseX, mouseY, metricsData, ...) and new signature (metricsData, x, y)
  let modalX: number;
  let modalY: number;

  if (typeof mouseXOrMetrics === 'object') {
    // New signature: open(project, address, metricsData, x, y, getMetrics?, circleExists?, getHasCircle?, savedStatus?)
    metrics.value = mouseXOrMetrics;
    modalX = mouseYOrX || 100;
    modalY = (metricsDataOrY as number) || 100;
    metricsGetter = getMetrics || null;
    hasCircle.value = savedStatusOrCircleExists; // 7th param is circleExists
    hasCircleGetter = getSavedStatusOrGetHasCircle || null; // 8th param is getHasCircle
    isSaved.value = circleExistsOrSavedStatus; // 9th param is savedStatus
    savedStatusGetter = null; // We'll update via hasCircleGetter interval for simplicity
  } else {
    // Old signature: open(project, address, mouseX, mouseY, metricsData, getMetrics, savedStatus, getSavedStatus)
    metrics.value = (metricsDataOrY as CircleMetrics) || null;
    metricsGetter = getMetrics || null;
    isSaved.value = savedStatusOrCircleExists; // 7th param is savedStatus
    savedStatusGetter = getSavedStatusOrGetHasCircle || null; // 8th param is getSavedStatus
    hasCircle.value = true; // Circle always exists when clicking on it
    hasCircleGetter = null;

    // Position near mouse, but ensure it stays in viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Center horizontally on mouse, with bounds checking
    modalX = mouseXOrMetrics - width.value / 2;
    modalX = Math.max(20, Math.min(modalX, viewportWidth - width.value - 20));

    // Position vertically with padding from mouse
    modalY = (mouseYOrX || 0) + 20;
    if (modalY + height.value > viewportHeight - 20) {
      modalY = (mouseYOrX || 0) - height.value - 20;
    }
    modalY = Math.max(20, modalY);
  }

  // Start live updates (every 200ms) if we have getters
  if (metricsUpdateInterval !== null) {
    clearInterval(metricsUpdateInterval);
  }
  if (metricsGetter || savedStatusGetter || hasCircleGetter) {
    metricsUpdateInterval = window.setInterval(() => {
      if (metricsGetter) {
        const newMetrics = metricsGetter();
        if (newMetrics) {
          metrics.value = newMetrics;
        }
      }
      // Update saved status
      if (savedStatusGetter) {
        isSaved.value = savedStatusGetter();
      }
      // Update hasCircle status (and also isSaved when we have new signature)
      if (hasCircleGetter) {
        const newHasCircle = hasCircleGetter();
        hasCircle.value = newHasCircle;
        // When circle is added, we need to start getting metrics and saved status
        // But for simplicity, isSaved updates through the button click
      }
    }, 200);
  }

  x.value = modalX;
  y.value = modalY;
  isOpen.value = true;

  // Trigger flash animation
  flashHeader();

  // console.log(`📋 Modal opened for ${project?.name || address}`, 'Metrics:', metrics.value, 'hasCircle:', hasCircle.value);
};

/**
 * Close modal
 */
const close = () => {
  isOpen.value = false;
  const closingProjectId = projectId.value;
  projectData.value = null;
  contractAddress.value = '';
  projectId.value = '';
  metrics.value = null;
  metricsGetter = null;
  isSaved.value = false;
  savedStatusGetter = null;
  hasCircle.value = false;
  hasCircleGetter = null;

  // Stop metrics updates
  if (metricsUpdateInterval !== null) {
    clearInterval(metricsUpdateInterval);
    metricsUpdateInterval = null;
  }

  emit('close', props.modalId);
  // console.log('📋 Modal closed:', closingProjectId);
};

/**
 * Minimize modal (hide but keep state)
 */
const minimize = () => {
  isOpen.value = false;
  const title = projectData.value?.name ||
    (contractAddress.value ? `${contractAddress.value.substring(0, 8)}...` : 'Unknown');
  emit('minimize', props.modalId, title);
  // console.log('📋 Modal minimized:', title);
};

/**
 * Restore modal from minimized state
 */
const restore = () => {
  isOpen.value = true;
  flashHeader();
  // console.log('📋 Modal restored:', projectData.value?.name || contractAddress.value);
};

/**
 * Handle add circle button click
 */
const handleAddCircle = () => {
  if (contractAddress.value && contractAddress.value !== 'not-tracked') {
    emit('addCircle', contractAddress.value);
    hasCircle.value = true; // Optimistically update UI
  }
};

/**
 * Handle save position button click
 */
const handleSavePosition = () => {
  if (contractAddress.value && contractAddress.value !== 'not-tracked') {
    emit('savePosition', contractAddress.value);
    isSaved.value = true; // Optimistically update UI
  }
};

/**
 * Handle unsave button click
 */
const handleUnsave = () => {
  if (contractAddress.value) {
    emit('unsave', contractAddress.value);
    isSaved.value = false; // Immediately update UI
  }
};

/**
 * Handle delete button click
 */
const handleDelete = () => {
  if (contractAddress.value) {
    emit('delete', contractAddress.value, projectData.value?.name);
    close(); // Close the modal after deleting
  }
};

/**
 * Bring modal to front (no flash - flash only on external focus)
 */
const bringToFront = () => {
  emit('focus', props.modalId);
};

// Cleanup on unmount
onUnmounted(() => {
  if (metricsUpdateInterval !== null) {
    clearInterval(metricsUpdateInterval);
    metricsUpdateInterval = null;
  }
});

/**
 * Check if a stat is pinned for this address
 */
const isPinned = (statType: AddressStatType): boolean => {
  if (!contractAddress.value) return false;
  const key = makeAddressStatKey(contractAddress.value, statType);
  return userSettings.isPinned(key);
};

/**
 * Toggle pin state for a stat
 */
const togglePin = (statType: AddressStatType): void => {
  if (!contractAddress.value) return;
  const key = makeAddressStatKey(contractAddress.value, statType);
  userSettings.togglePin(key);
};

// Expose methods for parent component
defineExpose({
  open,
  close,
  minimize,
  restore,
  setZIndex,
  flashHeader,
  getProjectId: () => projectId.value,
  getTitle: () => projectData.value?.name ||
    (contractAddress.value ? `${contractAddress.value.substring(0, 8)}...` : 'Unknown')
});
</script>

<style scoped>
.project-modal {
  position: fixed;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  font-family: Inter, Arial, sans-serif;
  user-select: none;
  box-shadow: var(--shadow-lg);
  border-radius: 4px;
}

/* Header */
.modal-header {
  height: 40px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  cursor: move;
  flex-shrink: 0;
  transition: background 0.3s;
  position: relative;
  border-radius: 4px 4px 0 0;
}

.modal-header.flashing {
  animation: headerFlash 500ms ease-out;
}

@keyframes headerFlash {
  0% {
    background: var(--accent-primary);
    opacity: 0.3;
  }
  100% {
    background: var(--bg-elevated);
  }
}

.modal-title {
  color: var(--accent-primary);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-buttons {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 6px;
}

.minimize-btn,
.close-btn {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-secondary);
  width: 24px;
  height: 24px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0;
}

.minimize-btn:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.close-btn {
  border-color: var(--error);
  color: var(--error);
}

.close-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.btn-icon {
  width: 14px;
  height: 14px;
}

/* Content */
.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  color: var(--text-primary);
  min-height: 0;
  position: relative;
}

.project-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Dapp Header - Icon + Info side by side */
.dapp-header {
  display: flex;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

.dapp-icon {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--bg-primary);
  display: block;
}

.dapp-icon.clickable {
  cursor: pointer;
  transition: border-color 0.2s, transform 0.2s;
}

.dapp-icon.clickable:hover {
  border-color: var(--accent-primary);
  transform: scale(1.02);
}

.dapp-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dapp-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.dapp-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dapp-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
  text-decoration: none;
}

.megamafia-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  color: #000;
  border-radius: 4px;
  white-space: nowrap;
}

.dapp-name.clickable {
  cursor: pointer;
  transition: color 0.2s;
}

.dapp-name.clickable:hover {
  color: var(--accent-primary);
}

.dapp-category {
  font-size: 11px;
  color: var(--accent-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

.dapp-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
}

.dapp-link {
  font-size: 12px;
  color: var(--success);
  text-decoration: none;
  transition: color 0.2s;
}

.dapp-link:hover {
  color: var(--accent-primary);
}

.dapp-address {
  font-size: 12px;
  color: var(--text-muted);
  text-decoration: none;
  font-family: monospace;
  word-break: break-all;
  transition: color 0.2s;
}

.dapp-address:hover {
  color: var(--accent-primary);
}

.dapp-description {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 6px 0 0 0;
}

.label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.address-only {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Saved Position Indicator */
.saved-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 140, 0, 0.1);
  border: 1px solid #ff8c00;
  border-radius: 4px;
  margin-top: 4px;
}

.saved-icon {
  width: 16px;
  height: 16px;
  color: #ff8c00;
  flex-shrink: 0;
}

.saved-text {
  color: #ff8c00;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

.unsave-btn {
  background: transparent;
  border: 1px solid #ff8c00;
  border-radius: 3px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s;
}

.unsave-btn:hover {
  background: #ff8c00;
}

.unsave-icon {
  width: 12px;
  height: 12px;
  color: #ff8c00;
}

.unsave-btn:hover .unsave-icon {
  color: var(--bg-surface);
}

/* Action Buttons (Add Circle, Save Position) */
.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: Inter, Arial, sans-serif;
}

.action-icon {
  width: 16px;
  height: 16px;
}

.add-circle-btn {
  background: transparent;
  border: 1px solid var(--accent-primary);
  color: var(--accent-primary);
}

.add-circle-btn:hover {
  background: var(--accent-primary);
  color: var(--bg-surface);
}

.save-position-btn {
  background: transparent;
  border: 1px solid #ff8c00;
  color: #ff8c00;
}

.save-position-btn:hover {
  background: #ff8c00;
  color: var(--bg-surface);
}

/* Not Tracked Section */
.not-tracked-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
  background: var(--hover-overlay);
  border: 1px dashed var(--border);
  border-radius: 8px;
  text-align: center;
}

.not-tracked-message {
  display: flex;
  align-items: center;
  gap: 10px;
}

.not-tracked-icon {
  font-size: 24px;
}

.not-tracked-text {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.not-tracked-description {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
  max-width: 280px;
}

/* Delete Section */
.delete-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.delete-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid var(--error);
  border-radius: 4px;
  color: var(--error);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.delete-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
}

.delete-icon {
  width: 14px;
  height: 14px;
}

.delete-note {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-muted);
  text-align: center;
  font-style: italic;
}

/* Metrics Section */
.metrics-section {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-radius: 4px;
  overflow: hidden;
}

.section-title {
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

/* Stat rows with alternating backgrounds */
.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
}

.stat-row.odd {
  background: var(--hover-overlay);
}

.stat-row.even {
  background: transparent;
}

.stat-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.stat-info .label {
  font-size: 13px;
  color: var(--text-secondary);
}

.stat-info .value {
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  color: var(--text-primary);
}

/* Pin button */
.pin-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pin-icon {
  width: 18px;
  height: 18px;
  color: var(--text-muted);
  transition: color 0.2s;
}

.pin-btn:hover .pin-icon {
  color: var(--accent-primary);
}

.pin-icon.pinned {
  color: var(--accent-primary);
}

/* Resize Handles */
.resize-handle {
  position: absolute;
  background: transparent;
}

.resize-right {
  right: 0;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
}

.resize-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 8px;
  cursor: ns-resize;
}

.resize-corner {
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  background: var(--accent-primary);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}

.resize-corner:hover {
  background: var(--success);
}

/* Custom scrollbar */
.modal-content::-webkit-scrollbar {
  width: 8px;
}

.modal-content::-webkit-scrollbar-track {
  background: var(--bg-primary);
}

.modal-content::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: var(--accent-primary);
}
</style>
